import React, { useState, useRef, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { Progress } from '@/components/ui/Progress';
import { Alert } from '@/components/ui/Alert';
import { Table } from '@/components/ui/Table';
import { Badge } from '@/components/ui/Badge';
import { StatCard } from '@/components/ui/StatCard';
import { AttendeeImportService, type ImportPreview, type ImportCommitResult } from '@/services/AttendeeImportService';
import { useNotificationStore } from '@/stores/notificationStore';
import { Steps, createSteps, updateStepStatus } from '@/components/ui';
import { 
  Upload, 
  FileText, 
  CheckCircle, 
  XCircle, 
  AlertTriangle,
  Download,
  Eye,
  Check,
  X,
  Loader2
} from 'lucide-react';

interface Props {
  eventId: string;
  isOpen: boolean;
  onClose: () => void;
  onImportComplete: () => void;
}

type ImportStep = 'upload' | 'processing' | 'preview' | 'committing' | 'complete';

export function AttendeeImport({ eventId, isOpen, onClose, onImportComplete }: Props) {
  const [currentStep, setCurrentStep] = useState<ImportStep>('upload');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [draftId, setDraftId] = useState<string | null>(null);
  const [importPreview, setImportPreview] = useState<ImportPreview | null>(null);
  const [commitResult, setCommitResult] = useState<ImportCommitResult | null>(null);
  const [processingStatus, setProcessingStatus] = useState<string>('pending');
  const [error, setError] = useState<string | null>(null);

  // Steps configuration
  const stepLabels = ['Upload File', 'Processing', 'Review Data', 'Importing', 'Complete'];
  const currentStepIndex = ['upload', 'processing', 'preview', 'committing', 'complete'].indexOf(currentStep);
  const steps = updateStepStatus(createSteps(stepLabels), currentStepIndex);
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  const importService = new AttendeeImportService();
  const notifySuccess = useNotificationStore(s => s.success);
  const notifyError = useNotificationStore(s => s.error);

  const handleReset = () => {
    setCurrentStep('upload');
    setSelectedFile(null);
    setDraftId(null);
    setImportPreview(null);
    setCommitResult(null);
    setProcessingStatus('pending');
    setError(null);
    setDragActive(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  const handleFileSelect = (file: File) => {
    const validation = importService.validateFile(file);
    if (!validation.valid) {
      notifyError(validation.error || 'Invalid file');
      return;
    }
    
    setSelectedFile(file);
    setError(null);
  };

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  }, []);

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const startImport = async () => {
    if (!selectedFile) return;

    setCurrentStep('processing');
    setError(null);

    try {
      console.log('🚀 Starting import for file:', selectedFile.name);
      
      // Start the import
      const result = await importService.startImport(eventId, selectedFile);
      console.log('✅ Import started successfully, draft_id:', result.draft_id);
      setDraftId(result.draft_id);

      // Small delay to ensure the job is queued before polling
      console.log('⏱️ Waiting 1 second before polling...');
      await new Promise(resolve => setTimeout(resolve, 1000));

      console.log('🔄 Starting to poll for status...');
      
      // Poll for completion
      const preview = await importService.pollImportStatus(
        eventId,
        result.draft_id,
        {
          onStatusUpdate: (status) => {
            console.log('📊 Status update:', status);
            setProcessingStatus(status);
          }
        }
      );

      console.log('✅ Polling completed, preview:', preview);
      setImportPreview(preview);
      setCurrentStep('preview');
      
      if (preview.errors.length > 0) {
        notifyError(`Import completed with ${preview.errors.length} errors. Please review before committing.`);
      } else {
        notifySuccess(`Successfully normalized ${preview.normalized_attendees.length} attendees.`);
      }

    } catch (error) {
      console.error('❌ Import failed at step:', { 
        currentStep, 
        error, 
        draftId,
        message: error instanceof Error ? error.message : 'Import failed',
        stack: error instanceof Error ? error.stack : undefined
      });
      setError(error instanceof Error ? error.message : 'Import failed');
      setCurrentStep('upload');
      
      // Show the actual error message to help debug
      notifyError(`Import failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const commitImport = async () => {
    if (!draftId) return;

    setCurrentStep('committing');
    setError(null);

    try {
      const result = await importService.commitImport(eventId, draftId);
      setCommitResult(result);
      setCurrentStep('complete');
      
      notifySuccess(`Import completed! Inserted: ${result.inserted}, Updated: ${result.updated}`);
      
      // Refresh attendee list
      onImportComplete();

    } catch (error) {
      console.error('Commit failed:', error);
      setError(error instanceof Error ? error.message : 'Commit failed');
      setCurrentStep('preview');
    }
  };

  const cancelImport = async () => {
    if (!draftId) {
      handleClose();
      return;
    }

    try {
      await importService.cancelImport(eventId, draftId);
      notifySuccess('Import cancelled');
    } catch (error) {
      notifyError('Failed to cancel import');
    }
    
    handleClose();
  };

  const renderUploadStep = () => (
    <div className="space-y-6">
      {/* File Drop Zone */}
      <div 
        className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
          dragActive 
            ? 'border-primary-500 bg-primary-50' 
            : 'border-gray-300 hover:border-gray-400'
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <Upload className="w-12 h-12 mx-auto mb-4 text-gray-400" />
        <div className="mb-4">
          <p className="text-lg font-medium mb-2">Drop your CSV file here</p>
          <p className="text-gray-500">or click to select a file</p>
        </div>
        <Button 
          variant="outline" 
          onClick={() => fileInputRef.current?.click()}
          className="mb-4"
        >
          <FileText className="w-4 h-4 mr-2" />
          Choose File
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,text/csv"
          onChange={handleFileInputChange}
          className="hidden"
        />
        <p className="text-sm text-gray-400">Maximum file size: 10MB</p>
      </div>

      {/* Selected File */}
      {selectedFile && (
        <Card>
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <FileText className="w-8 h-8 text-blue-500" />
                <div>
                  <p className="font-medium">{selectedFile.name}</p>
                  <p className="text-sm text-gray-500">
                    {importService.formatFileSize(selectedFile.size)}
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => setSelectedFile(null)}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Instructions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">CSV Format Requirements</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="prose prose-sm text-gray-600">
            <p>Your CSV file should include columns for:</p>
            <ul>
              <li><strong>Required:</strong> First name, Last name</li>
              <li><strong>Optional:</strong> Email, Phone, City, State/Region, Country, Address</li>
              <li><strong>Location fields</strong> are critical for flight cost estimation</li>
            </ul>
            <p>The system will automatically normalize and clean your data using AI.</p>
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => importService.downloadCSVTemplate()}
            className="mt-4"
          >
            <Download className="w-4 h-4 mr-2" />
            Download Template
          </Button>
        </CardContent>
      </Card>

      {error && (
        <Alert variant="error">
          <AlertTriangle className="w-4 h-4" />
          <div>
            <strong>Import Error</strong>
            <p>{error}</p>
          </div>
        </Alert>
      )}
    </div>
  );

  const renderProcessingStep = () => (
    <div className="space-y-6 text-center py-8">
      <Loader2 className="w-12 h-12 mx-auto animate-spin text-primary-600" />
      <div>
        <h3 className="text-lg font-semibold mb-2">Processing CSV File</h3>
        <p className="text-gray-500 mb-4">
          Our AI is normalizing and cleaning your attendee data...
        </p>
        <div className="max-w-xs mx-auto">
          <Progress 
            value={processingStatus === 'ready' ? 100 : 50} 
            className="mb-2" 
          />
          <p className="text-sm text-gray-600">
            Status: {processingStatus === 'pending' ? 'Parsing CSV' : 'Normalizing data'}
          </p>
        </div>
      </div>
    </div>
  );

  const renderPreviewStep = () => {
    if (!importPreview) return null;

    const { draft, normalized_attendees, errors } = importPreview;

    return (
      <div className="space-y-6">
        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <StatCard
            label="Total Rows"
            value={draft.total_rows}
            icon={<FileText className="w-5 h-5" />}
            iconColor="muted"
          />
          <StatCard
            label="Normalized"
            value={draft.normalized_count}
            valueColor="success"
            iconColor="success"
            icon={<CheckCircle className="w-5 h-5" />}
          />
          <StatCard
            label="Errors"
            value={draft.error_count}
            valueColor="error"
            iconColor="error"
            icon={<XCircle className="w-5 h-5" />}
          />
        </div>

        {/* Errors Section */}
        {errors.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-500" />
                Import Errors ({errors.length})
              </CardTitle>
              <CardDescription>
                The following rows could not be processed. They will be skipped during import.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="max-h-64 overflow-y-auto">
                <div className="space-y-3">
                  {errors.slice(0, 10).map((error, index) => (
                    <div key={index} className="p-3 bg-red-50 border border-red-200 rounded">
                      <p className="text-sm font-medium text-red-800">
                        Row {error.row_index + 1}: {error.message}
                      </p>
                      {error.field && (
                        <p className="text-xs text-red-600 mt-1">Field: {error.field}</p>
                      )}
                    </div>
                  ))}
                  {errors.length > 10 && (
                    <p className="text-sm text-gray-500 text-center">
                      ... and {errors.length - 10} more errors
                    </p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Preview Table */}
        {normalized_attendees.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Review: All Normalized Attendees ({normalized_attendees.length})</CardTitle>
              <CardDescription>
                Review all AI-normalized data before importing. Scroll through the entire list to ensure accuracy.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table
                data={normalized_attendees}
                columns={[
                  { 
                    key: 'full_name', 
                    title: 'Name',
                    render: (value) => <span className="font-medium">{value}</span>
                  },
                  { key: 'email', title: 'Email', render: (value) => value || '—' },
                  { key: 'phone', title: 'Phone', render: (value) => value || '—' },
                  { key: 'city', title: 'City', render: (value) => value || '—' },
                  { key: 'region', title: 'State/Region', render: (value) => value || '—' },
                  { key: 'country_iso2', title: 'Country', render: (value) => value || '—' },
                  { key: 'external_ref', title: 'Ref', render: (value) => value || '—' }
                ]}
                searchable={true}
                emptyText="No attendees to preview"
              />
            </CardContent>
          </Card>
        )}

        {error && (
          <Alert variant="error">
            <AlertTriangle className="w-4 h-4" />
            <div>
              <strong>Preview Error</strong>
              <p>{error}</p>
            </div>
          </Alert>
        )}
      </div>
    );
  };

  const renderCommittingStep = () => (
    <div className="space-y-6 text-center py-8">
      <Loader2 className="w-12 h-12 mx-auto animate-spin text-primary-600" />
      <div>
        <h3 className="text-lg font-semibold mb-2">Importing Attendees</h3>
        <p className="text-gray-500">
          Geocoding addresses and saving attendees to the database...
        </p>
      </div>
    </div>
  );

  const renderCompleteStep = () => {
    if (!commitResult) return null;

    return (
      <div className="space-y-6 text-center py-8">
        <CheckCircle className="w-16 h-16 mx-auto text-green-500" />
        <div>
          <h3 className="text-xl font-semibold mb-2">Import Complete!</h3>
          <p className="text-gray-500 mb-6">
            Your attendees have been successfully imported.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <StatCard
              label="Inserted"
              value={commitResult.inserted}
              valueColor="success"
              iconColor="success"
              icon={<CheckCircle className="w-5 h-5" />}
            />
            <StatCard
              label="Updated"
              value={commitResult.updated}
              valueColor="default"
              iconColor="primary"
              icon={<FileText className="w-5 h-5" />}
            />
            <StatCard
              label="Skipped"
              value={commitResult.skipped}
              valueColor="muted"
              iconColor="muted"
              icon={<XCircle className="w-5 h-5" />}
            />
          </div>

          {commitResult.errors.length > 0 && (
            <Alert variant="warning" className="mb-6">
              <AlertTriangle className="w-4 h-4" />
              <div>
                <strong>Some Issues Occurred</strong>
                <p>{commitResult.errors.length} attendees could not be processed.</p>
              </div>
            </Alert>
          )}
        </div>
      </div>
    );
  };

  const getStepTitle = () => {
    switch (currentStep) {
      case 'upload': return 'Upload CSV File';
      case 'processing': return 'Processing Data';
      case 'preview': return 'Review Import';
      case 'committing': return 'Importing Attendees';
      case 'complete': return 'Import Complete';
      default: return 'Import Attendees';
    }
  };

  const getFooterExplanation = () => {
    switch (currentStep) {
      case 'upload':
        return (
          <div className="text-sm text-gray-600 bg-blue-50 border border-blue-200 rounded-md p-3 mb-4">
            <p className="font-medium mb-1">📋 How duplicate handling works:</p>
            <p>If existing attendees are found, they will be <strong>updated</strong> rather than creating duplicates. 
            We match by <strong>email address first</strong>, then fall back to <strong>first + last name</strong> if no email match is found.</p>
          </div>
        );
      case 'preview':
        return (
          <div className="text-sm text-gray-600 bg-green-50 border border-green-200 rounded-md p-3 mb-4">
            <p className="font-medium mb-1">✅ Ready to import:</p>
            <p>This will save {importPreview?.normalized_attendees.length || 0} attendees to your event. 
            Any duplicates will be updated with the new information.</p>
          </div>
        );
      default:
        return null;
    }
  };

  const getFooterButtons = () => {
    switch (currentStep) {
      case 'upload':
        return (
          <>
            <Button variant="ghost" onClick={handleClose}>
              Cancel
            </Button>
            <Button 
              onClick={startImport} 
              disabled={!selectedFile}
              leftIcon={<Upload className="w-4 h-4" />}
            >
              Start Processing
            </Button>
          </>
        );
      case 'processing':
        return (
          <Button variant="ghost" onClick={cancelImport}>
            Cancel Import
          </Button>
        );
      case 'preview':
        return (
          <>
            <Button variant="ghost" onClick={cancelImport}>
              Cancel
            </Button>
            <Button 
              onClick={commitImport}
              disabled={!importPreview?.normalized_attendees.length}
              leftIcon={<Check className="w-4 h-4" />}
            >
              Import Now
            </Button>
          </>
        );
      case 'committing':
        return null; // No buttons during commit
      case 'complete':
        return (
          <Button onClick={handleClose} leftIcon={<CheckCircle className="w-4 h-4" />}>
            Done
          </Button>
        );
      default:
        return null;
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={currentStep === 'processing' || currentStep === 'committing' ? () => {} : handleClose}
      title={getStepTitle()}
      description={`Step ${['upload', 'processing', 'preview', 'committing', 'complete'].indexOf(currentStep) + 1} of 5`}
      size="xl"
    >
      <div className="space-y-6">
        {/* Progress Steps */}
        <Steps 
          steps={steps}
          responsive={true}
          showLabels={true}
          size="md"
          variant="default"
          className="mb-2"
        />

        {/* Step Content */}
        <div className="min-h-[400px]">
          {currentStep === 'upload' && renderUploadStep()}
          {currentStep === 'processing' && renderProcessingStep()}
          {currentStep === 'preview' && renderPreviewStep()}
          {currentStep === 'committing' && renderCommittingStep()}
          {currentStep === 'complete' && renderCompleteStep()}
        </div>

        {/* Footer */}
        <div className="pt-4 border-t">
          {getFooterExplanation()}
          <div className="flex justify-end gap-2">
            {getFooterButtons()}
          </div>
        </div>
      </div>
    </Modal>
  );
}